import type { ProductTourState } from '@elara/client-core/states/ProductTourState';

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@elara/client-core/lib/utils';
import { TOUR_STEPS } from '@elara/client-core/states/ProductTourState';

import { Button } from '#ui/button.js';
import { Logo } from '#ui/index.js';

interface TourOverlayProps {
  tourState: ProductTourState;
}

interface AnchorPosition {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

/** Finds the first visible element matching a selector */
function findVisibleElement(selector: string): Element | null {
  const elements = document.querySelectorAll(selector);
  for (const el of elements) {
    const htmlEl = el as HTMLElement;
    if (!htmlEl.offsetParent && htmlEl.style.position !== 'fixed') continue;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;
    return el;
  }
  return null;
}

const DIALOG_GAP = 12;

export function TourOverlay({ tourState }: TourOverlayProps): React.ReactNode {
  const stepConfig = tourState.currentStepConfig;
  const isIntro = stepConfig != null && 'intro' in stepConfig && stepConfig.intro;
  const isCentered = isIntro || tourState.isLastStep;
  const isAnchored = !isCentered && !!stepConfig?.anchorSelector;

  const [mounted, setMounted] = useState(false);
  const [anchor, setAnchor] = useState<AnchorPosition | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  // Track the anchor element's position
  const anchorSelector = stepConfig?.anchorSelector;
  const updateAnchor = useCallback(() => {
    if (!anchorSelector) {
      setAnchor(null);
      return;
    }
    const el = findVisibleElement(anchorSelector);
    if (!el) {
      setAnchor(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setAnchor({
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    });
  }, [anchorSelector]);

  // Measure before paint on step change for instant positioning
  useLayoutEffect(() => {
    updateAnchor();
  }, [updateAnchor]);

  // Keep position updated on resize/scroll
  useEffect(() => {
    window.addEventListener('resize', updateAnchor);
    window.addEventListener('scroll', updateAnchor, true);
    return () => {
      window.removeEventListener('resize', updateAnchor);
      window.removeEventListener('scroll', updateAnchor, true);
    };
  }, [updateAnchor]);

  if (!stepConfig) return null;

  // Compute anchored dialog position for desktop.
  // On mobile, Tailwind's max-md:!static overrides these inline styles so the
  // dialog falls back to the flex-based bottom-pinned layout.
  let dialogStyle: React.CSSProperties | undefined;
  if (isAnchored && anchor) {
    const clampedTop = Math.max(
      DIALOG_GAP,
      Math.min(anchor.top, window.innerHeight - (dialogRef.current?.offsetHeight ?? 200) - DIALOG_GAP),
    );
    dialogStyle = { position: 'fixed', left: anchor.right + DIALOG_GAP, top: clampedTop };
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/8 pointer-events-none transition-all backdrop-blur-none duration-500 ease-in-out',
          isIntro && 'backdrop-blur-lg bg-black/50',
        )}
      />

      {/* Dialog container */}
      <div
        className={cn(
          'fixed inset-0 z-50 flex p-4 pointer-events-none transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]',
          isAnchored ? 'items-end justify-center md:items-start md:justify-start' : 'items-center justify-center',
        )}
      >
        <div
          ref={dialogRef}
          className={cn(
            'pointer-events-auto rounded-lg dark:border',
            !isCentered && 'md:rounded-tl-none',
            isAnchored
              ? 'w-full max-w-md md:max-w-sm mb-12 md:mb-0 p-5 bg-background/95 backdrop-blur-md shadow-2xl max-md:!static max-md:!inset-auto transition-[top,left,opacity,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]'
              : cn(
                  'transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]',
                  'w-full max-w-md p-8 bg-background shadow-2xl',
                ),
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
          )}
          style={dialogStyle}
        >
          <div
            className={cn(
              'flex flex-col transition-all duration-400 ease-in-out',
              isCentered ? 'items-center text-center gap-6' : 'items-stretch text-left gap-3',
            )}
          >
            {/* Logo — only on intro */}
            {isIntro && (
              <div className="overflow-hidden">
                <Logo variant="symbol" width={48} height={48} />
              </div>
            )}

            {/* Headline + description */}
            <div>
              <h3
                className={cn(
                  'flex-1 font-display font-light transition-all duration-400 ease-in-out',
                  isCentered ? 'text-2xl' : 'text-lg font-medium',
                )}
              >
                {stepConfig.headline}
              </h3>
              <p
                className={cn(
                  'text-sm leading-relaxed transition-all duration-400 ease-in-out',
                  isCentered ? 'mt-3' : 'mt-1',
                )}
              >
                {stepConfig.description}
              </p>
            </div>

            {/* Progress dots */}
            <div
              className={cn(
                'flex w-full items-center',
                isCentered ? 'flex-col gap-6' : 'flex-row gap-2 justify-between',
              )}
            >
              <div className="flex items-center gap-1.5">
                {TOUR_STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      'h-1.5 rounded-full transition-all duration-300',
                      i === tourState.currentStep ? 'w-6 bg-accent' : 'w-1.5 bg-muted-foreground/30',
                    )}
                  />
                ))}
              </div>

              {/* Navigation buttons */}
              <div className={cn('flex items-center', isCentered ? 'gap-3 w-full' : 'justify-between')}>
                {isIntro ? (
                  <>
                    {/* Just closes the tour, leaving the member on the demo trip: no new trip. */}
                    <Button variant="outline" onClick={() => tourState.keepExploring()} className="flex-1">
                      Skip tour
                    </Button>
                    <Button onClick={() => tourState.advance()} className="flex-1">
                      Continue
                    </Button>
                  </>
                ) : tourState.isLastStep ? (
                  <>
                    <Button variant="outline" onClick={() => tourState.keepExploring()} className="flex-1">
                      Keep exploring
                    </Button>
                    <Button onClick={() => tourState.planNewTrip()} className="flex-1">
                      Plan my own trip
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      {!tourState.isFirstStep && (
                        <Button variant="ghost" size="sm" onClick={() => tourState.back()}>
                          Back
                        </Button>
                      )}
                      <Button size="sm" onClick={() => tourState.advance()}>
                        Next
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
